import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  DestroyRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, map, tap } from 'rxjs';

import { plural } from 'pluralize';
import { RelatedToType } from 'app/shared/enums';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Company } from 'app/modules/companies/companies.types';
import { Project, Property } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';

type GetProjectExistsResponse = { projects: Partial<Project>[] };
type CommonData = {
  pipelineId: number;
  disableItemSuggestion: boolean;
  existingProperties: Property[];
  existingCompanies: Observable<Company[]>;
  existingContacts: Observable<Contact[]>;
};

@Component({
  selector: 'app-custom-items-name',
  templateUrl: './custom-items-name.component.html',
  styleUrls: ['./custom-items-name.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomItemsNameComponent implements OnChanges {
  private lastTitleSearched = null;

  title = new UntypedFormControl(null);
  similarItems$: Observable<Partial<Project>[] | Property[] | Company[] | Contact[]>;

  get pipelineId(): number {
    return this.commonData.pipelineId;
  }

  get disableItemSuggestion(): boolean {
    return Boolean(this.commonData.disableItemSuggestion);
  }

  @Input() commonData: CommonData;
  @Input() setValues: { title?: string; name?: string };
  @Input() readonly: boolean;
  @Input() relatedToType: RelatedToType;

  @Output() onNavigateByUrl = new EventEmitter<string>();
  @Output() onValueChanged = new EventEmitter<{ title?: string; name?: string }>();
  @Output() onToggleLoading = new EventEmitter<void>();

  private get titleValue(): string {
    return this.title.value;
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(private readonly projectService: ProjectsService) {
    this.title.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
      if (this.relatedToType === RelatedToType.Project) {
        this.onValueChanged.emit({ title: value });
      } else {
        this.onValueChanged.emit({ name: value });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.setValues?.currentValue) {
      this.handleSetValuesChange();
    }
  }

  private handleSetValuesChange(): void {
    if (this.relatedToType === RelatedToType.Project) {
      if (this.setValues?.title) {
        this.title.setValue(this.setValues.title);
      }
    } else {
      if (this.setValues?.name) {
        this.title.setValue(this.setValues.name);
      }
    }
  }

  get formatTitle(): { singular: string; plural: string } {
    let singular: string;

    switch (this.relatedToType) {
      case RelatedToType.Project:
        singular = 'Deal';
        break;
      case RelatedToType.Property:
        singular = 'Property';
        break;
      case RelatedToType.Company:
        singular = 'Company';
        break;
      case RelatedToType.Contact:
        singular = 'Contact';
        break;
      default:
        singular = 'Item';
    }

    return {
      singular,
      plural: plural(singular).toLowerCase(),
    };
  }

  searchSimilarItems(): void {
    if (this.disableItemSuggestion) {
      return;
    }

    if (this.relatedToType === RelatedToType.Project) {
      this.onValueChanged.emit({ title: this.titleValue });
    } else {
      this.onValueChanged.emit({ name: this.titleValue });
    }

    if (!this.titleValue) {
      this.similarItems$ = of([]);
      return;
    }

    if (this.titleValue !== this.lastTitleSearched) {
      this.onToggleLoading.emit();
      this.lastTitleSearched = this.titleValue;
      switch (this.relatedToType) {
        case RelatedToType.Project:
          this.similarItems$ = this.projectService.getProjectExists(this.titleValue, this.commonData.pipelineId).pipe(
            takeUntilDestroyed(this.destroyRef),
            map((response: GetProjectExistsResponse) => response?.projects),
            tap(() => this.onToggleLoading.emit()),
          );
          break;
        case RelatedToType.Property:
          this.similarItems$ = of(
            this.commonData.existingProperties.filter(
              (property) => property.name.toLowerCase() === this.titleValue.toLowerCase(),
            ),
          ).pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(() => this.onToggleLoading.emit()),
          );
          break;
        case RelatedToType.Company:
          this.similarItems$ = this.commonData.existingCompanies.pipe(
            map((companies) =>
              companies.filter((company) => company.name.toLowerCase() === this.titleValue.toLowerCase()),
            ),
            takeUntilDestroyed(this.destroyRef),
            tap(() => this.onToggleLoading.emit()),
          );
          break;
        case RelatedToType.Contact:
          this.similarItems$ = this.commonData.existingContacts.pipe(
            map((contacts) =>
              contacts.filter((contact) => contact.name.toLowerCase() === this.titleValue.toLowerCase()),
            ),
            takeUntilDestroyed(this.destroyRef),
            tap(() => this.onToggleLoading.emit()),
          );
          break;
      }
    }
  }

  navigateTo(similarItem: Project | Property): void {
    switch (this.relatedToType) {
      case RelatedToType.Project:
        this.onNavigateByUrl.emit(`/projects/${similarItem.pipeline_id}/${similarItem.id}`);
        break;
      case RelatedToType.Property:
        this.onNavigateByUrl.emit(`/properties/${similarItem.id}`);
        break;
      case RelatedToType.Company:
        this.onNavigateByUrl.emit(`/companies/${similarItem.id}`);
        break;
      case RelatedToType.Contact:
        this.onNavigateByUrl.emit(`/contacts/${similarItem.id}`);
        break;
    }
  }
}
